import React, { useState, useEffect } from 'react';
import { Grid, Button } from '@mui/material';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { useDebounce } from 'use-debounce';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { fetchSmsTemplates } from 'actions/action-lookups';

import { IState } from 'interfaces/redux/IState';
import { ReactSelect } from 'components/form/field/react_select';

import CustomerDropdown from 'containers/application-manager/components/customer-dropdown';
import LoadingOverlay from 'components/loading-overlay/loading-overlay';
import { NumberOption } from 'interfaces/forms/types';
import { notifyError } from 'actions/action-notifications';
import { buildQaId } from 'utils/build-qa-id';

import { BulkSmsStyles } from './index.styles';

import { logger } from '../../winston-logger';

import { SectionTitleStyled } from '../../containers/application-manager/components/section-title';
import { SearchField } from '../../containers/application-manager/components/search-field';
import { DataTable } from './tables/sms-template-table';
import { AddNewSmsTemplateModal } from './modal/add-new-sms-template-modal';
import { LIAISON_CUSTOMER, PHARMACIST_CUSTOMER } from '../../constants/enums';

const searchFieldPlaceholder = 'Search by keyword';
const debounceTimeMs = 500;
const initialSearchTerm = '';
const initialValue = { label: 'All', value: -1 };
type ISmsTemplateManagerProps = WithStyles<typeof BulkSmsStyles>;
const getQaId = buildQaId('application-manager.users.user-manager', '.');

const SmsTemplateManager = (props: ISmsTemplateManagerProps): JSX.Element => {
  const dispatch = useDispatch();
  const { classes } = props;

  // # region redux state
  const user = useTypedSelector((state: IState) => state.auth.currentUser);
  const stateSelectedCustomerId = useTypedSelector(
    (state: IState) => state.filters.selectedCustomerId,
  );
  const customers = useTypedSelector((state: IState) => state.filters.customers);
  const allSmsPredefinedMessages = useTypedSelector(
    (state: IState) => state.lookups.smsPredefinedMessages,
  );
  const tagTypeLookup = useTypedSelector((state: IState) => state.lookups.patientNotesTagTypes);

  // # endregion

  // # region component states
  const [searchTerm, setSearchTerm] = React.useState<string>(initialSearchTerm);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [reloadTrigger, setReloadTrigger] = React.useState<Date | undefined>(undefined);
  const [reloadDataTrigger, setReloadDataTrigger] = React.useState<Date | undefined>(undefined);
  const [selectedCategories, setSelectedCategories] = React.useState<NumberOption[]>([]);
  const [categoryOptions, setCategoryOptions] = React.useState<NumberOption[]>([]);
  const [smsTemplateData, setSmsTemplateData] = React.useState<any | undefined>(undefined);
  const [smsTemplate, setSmsTemplate] = React.useState<any>({ result: [] });
  const [addModalOpen, setAddModalOpen] = React.useState<boolean>(false);
  const [allCustomers, setAllCustomers] = React.useState<boolean>(false);

  const [selectedCustomerIds, setSelectedCustomerIds] = React.useState<any>([]);
  const [debouncedSearchTerm] = useDebounce<string | undefined>(searchTerm, debounceTimeMs);

  //   const userHasPermissionToAddSmsTemplate = React.useMemo<boolean>(() => {
  //     return UserUtils.userIsPermitted(UserPermissions.ApplicationManagerCpmpEdit);
  //   }, [user]);

  const userHasPermissionToAddSmsTemplate = ![LIAISON_CUSTOMER, PHARMACIST_CUSTOMER].includes(
    user?.role,
  );
  const userHasPermissionToEditSmsTemplate = ![LIAISON_CUSTOMER, PHARMACIST_CUSTOMER].includes(
    user?.role,
  );

  const DataTableContext = React.createContext(smsTemplateData);

  // # endregion

  // # region useEffect
  React.useEffect(() => {
    setLoading(true);
    (async () => {
      dispatch(fetchSmsTemplates());

      try {
        const categoryArr =
          tagTypeLookup && tagTypeLookup.length > 0
            ? tagTypeLookup.map(t => ({
                label: t.tag_type,
                value: t.id,
              }))
            : [];

        setCategoryOptions(categoryArr);
      } catch (error) {
        dispatch(fetchSmsTemplates());

        logger.error(error);
        dispatch(notifyError('Error clinical data types categories'));
      } finally {
        dispatch(fetchSmsTemplates());
        setLoading(false);
      }
    })();
  }, []);

  // #endregion

  // #region renders

  const renderEnableSmsTemplateModal = (): JSX.Element => {
    return (
      <AddNewSmsTemplateModal
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onSuccess={() => setReloadDataTrigger(new Date())}
        editing={false}
      />
    );
  };
  // #endregion

  return (
    <>
      {userHasPermissionToAddSmsTemplate && renderEnableSmsTemplateModal()}
      <Grid item xs={12}>
        <Grid item xs={12}>
          <SectionTitleStyled title="SMS Template Manager" />
        </Grid>
        <Grid item xs="auto">
          <Grid container spacing={0} alignItems="center">
            <Grid item xs={3}>
              <Grid container alignItems="center">
                <Grid item xs={12} className={classes.filterDropdownCustomers}>
                  <CustomerDropdown
                    selectedCustomerId={selectedCustomerIds}
                    setSelectedCustomerId={customerId => {
                      setSelectedCustomerIds(customerId);
                    }}
                    isMulti
                    isDisabled={allCustomers}
                    isLabelOn
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={3} className={classes.filterDropdown}>
              <Grid container justifyContent="flex-start">
                <LoadingOverlay open={loading} />
                <ReactSelect
                  label="Category"
                  name="category"
                  isMulti
                  value={
                    selectedCategories.length === 0
                      ? [initialValue]
                      : selectedCategories.filter(x => x.value !== initialValue.value)
                  }
                  fields={categoryOptions}
                  handleOnChange={(value: NumberOption[] | undefined) => {
                    setSelectedCategories(value ? value.filter(v => v.value > 0) : []);
                  }}
                />
              </Grid>
            </Grid>
            <Grid item xs={4} className={classes.searchField}>
              <Grid container justifyContent="flex-start">
                <SearchField
                  width={48}
                  value={searchTerm}
                  onChange={setSearchTerm}
                  initialValue={initialSearchTerm}
                  placeholder={searchFieldPlaceholder}
                />
              </Grid>
            </Grid>
            {userHasPermissionToAddSmsTemplate && (
              <Grid item xs={2} className={classes.addButtonContainer}>
                <Grid container justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setAddModalOpen(true)}
                    data-qa-id={getQaId('button.add-user')}
                  >
                    Add SMS Template
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
        <DataTableContext.Provider value={smsTemplate}>
          <DataTable
            searchTerm={debouncedSearchTerm}
            triggerReload={reloadTrigger}
            handleReloadDataTrigger={(date: React.SetStateAction<Date | undefined>) =>
              setReloadTrigger(date)
            }
            userHasPermissionToEditSmsTemplate={userHasPermissionToEditSmsTemplate} // TODO fix permissions
            activeAnyField={searchTerm.length > 0}
            customerIds={selectedCustomerIds}
            categories={selectedCategories}
          />
        </DataTableContext.Provider>
      </Grid>
    </>
  );
};

export default withStyles(BulkSmsStyles)(SmsTemplateManager);
